import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Typography, Menu, MenuItem, Divider, IconButton } from "@mui/material";
import backgroundimage from "../image/bigscreen.jpg";
import colors from "../colour";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, set } from 'date-fns';
import moment from "moment";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import Globe from 'globe.gl';
import { UseAccessLevel } from "../AccessLevelContext";

// API
import { getUserDetails } from "../api/user";
import { fetchWeather, fetchLocation } from "../api/others";
import { buildingDataAPI, solarDataAPI, gridwithEcosDataAPI, } from "../api/meter";
import { sm_loadDataAPI, sm_solarDataAPI, sm_gridDataAPI, } from "../api/smartmeter";
import { solarAnalysisData, gridAnalysisData, loadAnalysisData, detailRP4BillData, SMsolarAnalysisData, SMgridAnalysisData, SMloadAnalysisData, detailSmartMeterBillData, reading_values } from "../api/bill";
import { getPlantList, accumulated_solar, total_solar_generated } from "../api/plant";
import SLD from "../components/SLD";

//weather icons and bg
import clearsky_day from '../weatherimage/clearsky_day.png'
import cloudy_day from '../weatherimage/cloudy_day.png'
import raining_day from '../weatherimage/raining_day.png'
import thunderstorm_day from '../weatherimage/thunderstorm_day.png'
import clearsky_night from '../weatherimage/clearsky_night.png'
import cloudy_night from '../weatherimage/cloudy_night.png'
import raining_night from '../weatherimage/raining_day.png'
import thunderstorm_night from '../weatherimage/thunderstorm_day.png'
import ClearIcon from '@mui/icons-material/Clear';
import MapIcon from '@mui/icons-material/Map';
import {
    PlayArrow as PlayArrowIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
    SkipPrevious as SkipPreviousIcon,
    SkipNext as SkipNextIcon,
    ArrowBack as ArrowBackIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';


const BigScreen = () => {
    // const [weatherData, setWeatherData] = useState(null); //weather
    const [locationData, setLocationData] = useState(null);
    const [userData, setUserData] = useState(null);
    const [time, setTime] = useState(new Date()); // Initial time state set to current date and time
    const { accessLevel } = UseAccessLevel();

    // Fetch Solar Grid Load Data
    const [solarData, setSolarData] = useState([]);
    const [BuildingLoad, setBuildingLoad] = useState([]);
    const [gridWithEcosData, setGridWithEcosData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [smloadData, setsmloadData] = useState([]);
    const [smsolarData, setsmsolarData] = useState([]);
    const [smgridData, setsmgridData] = useState([]);
    const [accumulatedSolar, setAccumulatedSolar] = useState([]);
    const [totalSolar, setTotalSolar] = useState([]);

    // Energy Analysis
    const [solarAnalysis, setSolarAnalysis] = useState([]);
    const [gridAnalysis, setGridAnalysis] = useState([]);
    const [loadAnalysis, setLoadAnalysis] = useState([]);
    const [billDetail, setBillDetail] = useState(null);
    const [billDetaillastmonth, setBillDetaillastmonth] = useState(null);

    // Sites List
    const [clientList, setClientList] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [hasSmartMeter, setHasSmartMeter] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const [selectedClient, setSelectedClient] = useState(null);

    // Auto-loop states
    const [isAutoLoopMode, setIsAutoLoopMode] = useState(false);
    const [allClients, setAllClients] = useState([]);
    const [currentClientIndex, setCurrentClientIndex] = useState(0);
    const [autoPlaySpeed, setAutoPlaySpeed] = useState(10); // seconds
    const [autoLoopInterval, setAutoLoopInterval] = useState(null);
    const [isAutoLoopPaused, setIsAutoLoopPaused] = useState(false);


    // Handle selected client from Map.jsx
    useEffect(() => {

        // Check for AUTO-LOOP mode first
        if (location.state?.autoLoopMode) {


            const clients = location.state.allClients || [];
            const speed = location.state.autoPlaySpeed || 10;
            const initialIndex = location.state.currentIndex || 0;


            if (clients.length > 0) {
                // Set auto-loop states
                setIsAutoLoopMode(true);
                setAllClients(clients);
                setAutoPlaySpeed(speed);
                setCurrentClientIndex(initialIndex);

                // Set the first client
                const firstClient = clients[initialIndex];


                setSelectedClient(firstClient);
                setUserData(firstClient);
                setHasSmartMeter(Boolean(firstClient.has_smart_meter));
            } else {
                console.error('❌ No clients provided for auto-loop');
                // Fallback to single client or redirect
                navigate("/Map");
            }
        }
        // Check for SINGLE CLIENT mode
        else if (location.state?.selectedClient) {


            const client = location.state.selectedClient;


            // Set single client mode
            setIsAutoLoopMode(false);
            setSelectedClient(client);
            setUserData(client);
            setHasSmartMeter(Boolean(client.has_smart_meter));

            // Clear auto-loop data
            setAllClients([]);
            setCurrentClientIndex(0);
        }
        // NO CLIENT DATA - handle fallback
        else {

            const fetchUser = async () => {
                try {
                    const user = await getUserDetails();
                    setUserData(user);
                    setSelectedClient(user);
                    setHasSmartMeter(Boolean(user.has_smart_meter));
                } catch (error) {
                    console.error("Error fetching user:", error);
                    navigate("/Map");
                }
            };
            fetchUser();
        }
    }, [location, navigate]);

    // Animations
    const fadeIn = {
        hidden: { opacity: 0, y: -30 },
        visible: { opacity: 2, y: 0, transition: { duration: 1.5, ease: "easeOut" } },
    };

    // Time Update (Efficient Interval)
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // const weatherCodes = useMemo(() => ({
    //     clearSky: [0],
    //     cloudySky: [1, 2, 3, 45, 48, 77],
    //     lightRaining: [51, 56, 57, 61, 66, 80],
    //     thunderstorm: [63, 65, 67, 81, 82, 53, 55, 95, 96, 99],
    // }), []);

    // const getWeatherCategory = useCallback((code) => {
    //     if (weatherCodes.clearSky.includes(code)) return "Clear Sky";
    //     if (weatherCodes.cloudySky.includes(code)) return "Cloudy Sky";
    //     if (weatherCodes.lightRaining.includes(code)) return "Light Raining";
    //     if (weatherCodes.thunderstorm.includes(code)) return "Thunderstorm";
    //     return "Loading...";
    // }, [weatherCodes]);

    const isDayTime = () => {
        const hour = new Date().getHours();
        return hour >= 6 && hour < 19;
    };

    // const getWeatherIcon = useCallback((weatherCode) => {
    //     const icons = isDayTime()
    //         ? { "Clear Sky": clearsky_day, "Cloudy Sky": cloudy_day, "Light Raining": raining_day, "Thunderstorm": thunderstorm_day }
    //         : { "Clear Sky": clearsky_night, "Cloudy Sky": cloudy_night, "Light Raining": raining_night, "Thunderstorm": thunderstorm_night };

    //     return icons[weatherCode] ?? (isDayTime() ? clearsky_day : clearsky_night);
    // }, []);

    // const weatherDescription = useMemo(() => getWeatherCategory(weatherData?.current_weather?.weathercode), [weatherData, getWeatherCategory]);
    // const weatherIcon = useMemo(() => getWeatherIcon(weatherDescription), [weatherDescription, getWeatherIcon]);

    // Fetch User Data
    useEffect(() => {
        if (!selectedClient?.client_id) return;

        let intervalId;

        const resetStates = () => {
            // setWeatherData(null);
            setLocationData(null);
            setBuildingLoad([]);
            setSolarData([]);
            setGridWithEcosData([]);
            setsmloadData([]);
            setsmsolarData([]);
            setsmgridData([]);
            setSolarAnalysis(null);
            setGridAnalysis(null);
            setLoadAnalysis(null);
            setBillDetail(null);
            setBillDetaillastmonth(null);
            setAccumulatedSolar([]);
            setTotalSolar([]);
        };

        const fetchData = async () => {
            setIsLoading(true);
            resetStates();

            try {
                const clientId = selectedClient.client_id;
                const lat = selectedClient.latitude;
                const lon = selectedClient.longitude;
                const interval = 1800;
                const formattedDate = moment().format("YYYY-MM-DD");
                const formattedMonth = moment().format("YYYY-MM");
                const lastMonthDate = moment().subtract(1, 'month').format("YYYY-MM");

                // 1️⃣ Fetch AFA/NEM for current and last month in parallel
                const [currentReading, lastReading] = await Promise.all([
                    reading_values(clientId, formattedMonth),
                    reading_values(clientId, lastMonthDate)
                ]);

                const currentAfa = currentReading?.data?.afa?.toString() || "0";
                const currentNem = currentReading?.data?.nem?.toString() || "0";
                const lastAfa = lastReading?.data?.afa?.toString() || "0";
                const lastNem = lastReading?.data?.nem?.toString() || "0";

                // 2️⃣ Update smart meter status
                const clientHasSmartMeter = Boolean(selectedClient.has_smart_meter);
                setHasSmartMeter(clientHasSmartMeter);

                // 3️⃣ Fetch weather & location in parallel
                // const [weather, location] = await Promise.all([
                //     fetchWeather(lat, lon),
                //     fetchLocation(lat, lon)
                // ]);
                // setWeatherData(weather);
                // setLocationData(location.data);

                if (clientHasSmartMeter) {
                    // 4️⃣ Smart meter data
                    const [smLoad, smSolar, smGrid,] = await Promise.all([
                        sm_loadDataAPI(clientId, formattedDate, interval),
                        sm_solarDataAPI(clientId, formattedDate, interval),
                        sm_gridDataAPI(clientId, formattedDate, interval),

                    ]);
                    setsmloadData(smLoad.data || []);
                    setsmsolarData(smSolar.data || []);
                    setsmgridData(smGrid.data || []);


                    // 5️⃣ Analysis data
                    const [solarAnalysisRes, gridAnalysisRes, loadAnalysisRes] = await Promise.all([
                        SMsolarAnalysisData(clientId, formattedMonth),
                        SMgridAnalysisData(clientId, formattedMonth),
                        SMloadAnalysisData(clientId, formattedMonth)
                    ]);
                    setSolarAnalysis(solarAnalysisRes.data);
                    setGridAnalysis(gridAnalysisRes.data);
                    setLoadAnalysis(loadAnalysisRes.data);

                    // 6️⃣ Bill data for current and last month
                    const [currentMonthBill, lastMonthBill] = await Promise.all([
                        detailSmartMeterBillData(clientId, formattedMonth, currentAfa, currentNem),
                        detailSmartMeterBillData(clientId, lastMonthDate, lastAfa, lastNem)
                    ]);
                    setBillDetail(currentMonthBill.data);
                    setBillDetaillastmonth(lastMonthBill.data);

                    const [accumulatedSolar, totalSolar] = await Promise.all([
                        accumulated_solar(clientId, formattedDate),
                        total_solar_generated(clientId, formattedMonth)
                    ]);
                    setAccumulatedSolar(accumulatedSolar.data || []);
                    setTotalSolar(totalSolar.data || []);

                } else {
                    // 4️⃣ RP4 data
                    const [buildingRes, solarRes, gridRes,] = await Promise.all([
                        buildingDataAPI(clientId, formattedDate, interval),
                        solarDataAPI(clientId, formattedDate, interval),
                        gridwithEcosDataAPI(clientId, formattedDate, interval),

                    ]);
                    setBuildingLoad(buildingRes.data || []);
                    setSolarData(solarRes.data || []);
                    setGridWithEcosData(gridRes.data || []);


                    // 5️⃣ Analysis data
                    const [solarAnalysisRes, gridAnalysisRes, loadAnalysisRes] = await Promise.all([
                        solarAnalysisData(clientId, formattedMonth),
                        gridAnalysisData(clientId, formattedMonth),
                        loadAnalysisData(clientId, formattedMonth)
                    ]);
                    setSolarAnalysis(solarAnalysisRes.data);
                    setGridAnalysis(gridAnalysisRes.data);
                    setLoadAnalysis(loadAnalysisRes.data);

                    const [accumulatedSolar, totalSolar] = await Promise.all([
                        accumulated_solar(clientId, formattedDate),
                        total_solar_generated(clientId, formattedMonth)
                    ]);
                    setAccumulatedSolar(accumulatedSolar.data || []);
                    setTotalSolar(totalSolar.data || []);

                    // 6️⃣ Bill data (current month only, last month optional)
                    const currentBill = await detailRP4BillData(clientId, formattedMonth, currentAfa, currentNem);
                    setBillDetail(currentBill.data);
                }

            } catch (err) {
                console.error("Error fetching client data:", err);
            } finally {
                setIsLoading(false);
            }
        };

        // Initial fetch
        fetchData();

        // Auto-refresh every 30 minutes
        intervalId = setInterval(fetchData, 30 * 60 * 1000);

        // Cleanup
        return () => clearInterval(intervalId);

    }, [selectedClient]);

    const createChartData = (datasets, keys) => {
        // Ensure datasets are defined and not null
        const sanitizedDatasets = datasets.map((dataset) => dataset || []);

        // Check if all datasets are empty
        if (sanitizedDatasets.every((dataset) => dataset.length === 0)) return [];

        return sanitizedDatasets[0].map((item, index) => {
            const time = Object.values(item)[0]; // Extract the time value
            const modified_time = moment.parseZone(time).format("YYYY-MM-DD HH:mm:ss"); // Parse while preserving the time and ignoring timezone

            const chartData = { timestamp: modified_time };

            sanitizedDatasets.forEach((dataset, datasetIndex) => {
                const key = keys[datasetIndex];
                if (key) {
                    const value = dataset[index]?.[key];
                    // Replace `null` or `undefined` with `NaN`, keep `0` as `0`
                    chartData[key] = value === null || value === undefined ? NaN : value;
                }
            });

            return chartData;
        });
    };

    // // Initialize Globe
    const globeRef = useRef(null);
    // Globe Setup
    useEffect(() => {
        if (!globeRef.current) return;

        const world = Globe()(globeRef.current)
            .globeImageUrl("//unpkg.com/three-globe/example/img/earth-blue-marble.jpg")
            .backgroundColor("rgba(0, 0, 0, 0)")
            .width(globeRef.current.clientWidth)
            .height(globeRef.current.clientHeight);

        world.controls().autoRotate = true;
        world.controls().autoRotateSpeed = 1.0;

        const handleResize = () => {
            world.width(globeRef.current.clientWidth);
            world.height(globeRef.current.clientHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch Client List
    useEffect(() => {
        const fetchPlantList = async () => {
            try {
                const plantList = await getPlantList();
                if (Array.isArray(plantList.data)) {
                    // Filter clients when setting state
                    const filteredClients = plantList.data.filter(client =>
                        client.access_level === 1 &&
                        (client.user_type === 0 || client.user_type === 1)
                    );

                    setClientList(filteredClients);
                }
            } catch (error) {
                console.error("Error fetching client list:", error);
            }
        };
        fetchPlantList();
    }, []);
    // Dropdown Handlers
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleSelect = (customer) => {
        setSelectedClient(customer);
        setUserData(customer);
        handleClose();
    };
    useEffect(() => {
        if (userData && userData.has_smart_meter !== undefined) {
            setHasSmartMeter(Boolean(userData.has_smart_meter));
        }
    }, [userData]);

    // sum of the  graph
    function aggregateMeterData(meters = []) {
        const combined = {};

        meters.forEach(meter => {
            if (!Array.isArray(meter.data)) return; // skip if meter.data is undefined or not an array

            meter.data.forEach(point => {
                const time = point.recorded_at;
                if (!combined[time]) {
                    combined[time] = { recorded_at: time, total_active_power_kw: null };
                }

                const currentValue = point.total_active_power_kw;

                if (currentValue !== null && !isNaN(currentValue)) {
                    if (combined[time].total_active_power_kw === null) {
                        combined[time].total_active_power_kw = currentValue;
                    } else {
                        combined[time].total_active_power_kw += currentValue;
                    }
                }
            });
        });

        return Object.values(combined);
    }

    const aggregatedData = aggregateMeterData(smgridData);
    const aggregatedsolarData = aggregateMeterData(smsolarData);

    function combineChartData(smloadData, aggregatedData, aggregatedsolarData) {
        // Create maps for faster lookup
        const gridMap = new Map(aggregatedData.map(item => [item.recorded_at, item.total_active_power_kw]));
        const solarMap = new Map(aggregatedsolarData.map(item => [item.recorded_at, item.total_active_power_kw]));

        return smloadData.map(item => ({
            timestamp: item.recorded_at,
            building_load_kw: item.total_active_power_kw,
            grid_power_kw: gridMap.get(item.recorded_at) ?? null,
            solar_generation_kw: solarMap.get(item.recorded_at) ?? null,
        }));
    }

    const combinedData = combineChartData(smloadData, aggregatedData, aggregatedsolarData);


    //donut chart
    const solar = parseFloat(solarAnalysis?.['total_solar_generated_kWh'] || 0);
    const grid = parseFloat(gridAnalysis?.['total_grid_consumption_kWh'] || 0);
    const total = solar + grid;
    const donutData = [
        {
            name: 'Solar',
            value: solar,
            percent: total > 0 ? (solar / total) * 100 : 0,
            sliceColor: colors.bsf1,
            labelColor: colors.bsf1,
        },
        {
            name: 'Grid',
            value: grid,
            percent: total > 0 ? (grid / total) * 100 : 0,
            sliceColor: colors.bsg1,
            labelColor: colors.bsg1,
        },
    ];

    // Custom chart label renderer
    const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, index }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius - 18;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        const entry = donutData[index];
        const label = `${entry.percent.toFixed(1)}%`;

        // Estimate text dimensions
        const padding = 4;
        const fontSize = 12; // in px — match your style
        const textWidth = label.length * 7; // estimate width (7px per char)
        const rectWidth = textWidth + padding * 2;
        const rectHeight = fontSize + padding * 2;

        return (
            <g>
                {/* Background rectangle */}
                <rect
                    x={x - rectWidth / 2}      // X position (centered on label)
                    y={y - rectHeight / 2}     // Y position (centered on label)
                    width={rectWidth}          // Width of the background box
                    height={rectHeight}        // Height of the background box
                    rx={4}                     // Rounded corners (horizontal)
                    ry={4}                     // Rounded corners (vertical)
                    fill="rgba(56, 51, 51, 0.76)"               // Background color of the frame
                />

                {/* Percentage text */}
                <text
                    x={x}
                    y={y}
                    fill={entry.labelColor}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                        fontSize: "0.7vw",
                        fontWeight: "bold",
                    }}
                >
                    {label}
                </text>
            </g>
        );
    };



    // BigScreen.jsx - Replace your auto-loop useEffect with this:
    useEffect(() => {
        // Clear any existing interval
        if (autoLoopInterval) {
            clearInterval(autoLoopInterval);
            setAutoLoopInterval(null);
        }

        // Only set up interval if in auto-loop mode, have clients, and not paused
        if (isAutoLoopMode && allClients.length > 0 && !isAutoLoopPaused) {

            const interval = setInterval(() => {
                setCurrentClientIndex((prevIndex) => {
                    // Calculate next index
                    const nextIndex = (prevIndex + 1) % allClients.length;


                    // If we're about to wrap around (completed all clients in BigScreen)
                    if (nextIndex === 0) {

                        // Clear interval first
                        clearInterval(interval);

                        // Navigate back to Map to continue the infinite loop
                        setTimeout(() => {
                            navigate("/Map", {
                                state: {
                                    continueLoop: true,  // This tells Map to restart the loop
                                    autoPlaySpeed: autoPlaySpeed,
                                    allClients: allClients,
                                    currentIndex: 0,
                                    cycleCount: (location.state?.cycleCount || 0) + 1,
                                    message: "Returning to Map to continue infinite loop"
                                }
                            });
                        }, 1000); // 1 second delay before navigation

                        return prevIndex; // Keep showing current client
                    }

                    // Otherwise, move to next client
                    const nextClient = allClients[nextIndex];

                    setSelectedClient(nextClient);
                    setUserData(nextClient);
                    setHasSmartMeter(Boolean(nextClient.has_smart_meter));
                    return nextIndex;
                });
            }, autoPlaySpeed * 1000);

            setAutoLoopInterval(interval);

            return () => {
                if (interval) clearInterval(interval);
            };
        }
    }, [isAutoLoopMode, allClients, autoPlaySpeed, isAutoLoopPaused, navigate, location.state]);
    // Update stopAutoLoop to clear everything properly
    const stopAutoLoop = () => {
        // Clear interval
        if (autoLoopInterval) {
            clearInterval(autoLoopInterval);
            setAutoLoopInterval(null);
        }

        // Reset all auto-loop states
        setIsAutoLoopMode(false);
        setIsAutoLoopPaused(false);
        setAllClients([]);
        setCurrentClientIndex(0);

        // Navigate back to Map WITHOUT continueAutoLoop flag
        navigate("/Map", {
            state: {
                autoLoopStopped: true,
                message: "Auto-loop stopped by user"
            }
        });
    };

    // Update goToNextClient to handle infinite loop
    const goToNextClient = () => {
        if (allClients.length === 0) return;

        const nextIndex = (currentClientIndex + 1) % allClients.length;

        // If we're about to go back to first client (completed a cycle in BigScreen)
        if (nextIndex === 0) {
            // Navigate back to Map to continue the infinite loop
            navigate("/Map", {
                state: {
                    continueAutoLoop: true,
                    autoPlaySpeed: autoPlaySpeed,
                    allClients: allClients,
                    currentIndex: nextIndex,
                    cycleCount: (location.state?.cycleCount || 0) + 1
                }
            });
            return;
        }

        const nextClient = allClients[nextIndex];
        setCurrentClientIndex(nextIndex);
        setSelectedClient(nextClient);
        setUserData(nextClient);
        setHasSmartMeter(Boolean(nextClient.has_smart_meter));
    };

    // Update navigation functions to reset completion state
    const goToPreviousClient = () => {
        if (allClients.length === 0) return;


        const prevIndex = (currentClientIndex - 1 + allClients.length) % allClients.length;
        const prevClient = allClients[prevIndex];

        setCurrentClientIndex(prevIndex);
        setSelectedClient(prevClient);
        setUserData(prevClient);
        setHasSmartMeter(Boolean(prevClient.has_smart_meter));
    };

    // Control functions
    const pauseAutoLoop = () => {
        setIsAutoLoopPaused(true);

    };

    const resumeAutoLoop = () => {
        setIsAutoLoopPaused(false);

    };


    const solarTrees = parseFloat(solarAnalysis?.equivalent_trees_planted) || 0;
    const gridTrees = -(parseFloat(gridAnalysis?.equivalent_trees_planted) || 0);
    const loadTrees = -(parseFloat(loadAnalysis?.equivalent_trees_planted) || 0);
    const maxValue = Math.max(
        Math.abs(solarTrees),
        Math.abs(gridTrees),
        Math.abs(loadTrees)
    );
    const ImpactRow = ({ label, value, maxPositive, maxNegative }) => {

        const barWidth = value > 0
            ? (value / maxPositive) * 80
            : (Math.abs(value) / maxNegative) * 80;

        return (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, height: "100%" }}>

                {/* Label */}
                <Box sx={{ width: "15%", fontWeight: "bold", fontSize: "1vw", }}>
                    {label}
                </Box>

                {/* Chart */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>

                    {/* LEFT SIDE */}
                    <Box sx={{ width: "50%", display: "flex", justifyContent: "flex-end" }}>
                        {value < 0 && (
                            <Box
                                sx={{
                                    width: `${barWidth}%`,
                                    height: "2vh",
                                    backgroundColor: "#e74c3c",
                                    borderRadius: 0.5,
                                    transition: "width 0.8s ease",
                                    mr: 0.2
                                }}
                            />
                        )}
                    </Box>

                    {/* CENTER AXIS */}
                    <Box sx={{ width: "2px", height: "2vh", backgroundColor: "#999" }} />

                    {/* RIGHT SIDE */}
                    <Box sx={{ width: "50%", display: "flex", justifyContent: "flex-start" }}>
                        {value > 0 && (
                            <Box
                                sx={{
                                    width: `${barWidth}%`,
                                    height: "2vh",
                                    backgroundColor: "#2ecc71",
                                    borderRadius: 0.5,
                                    transition: "width 0.8s ease",
                                    ml: 0.2
                                }}
                            />
                        )}
                    </Box>

                </Box>

                {/* Value */}
                <Box sx={{ width: "25%", textAlign: "right", fontWeight: "bold", fontSize: "1vw", }}>
                    {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Box>

            </Box>
        );
    };
    const maxPositive = Math.max(solarTrees, 1);
    const maxNegative = Math.max(Math.abs(gridTrees), Math.abs(loadTrees), 1);

    return (
        <Box sx={{ position: "relative", height: "100vh", width: "100vw", display: "flex", background: `url(${backgroundimage})`, backgroundSize: "100%", backgroundPosition: "center", }}>
            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", }}>
                {/* Header*/}
                <Box sx={{ width: "100%", height: "10%", display: "flex", justifyContent: "center", alignItems: "center", background: colors.bs4, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, clipPath: "polygon(0% 0%, 100% 0%, 100% 50%, 70% 50%, 67% 100%, 33% 100%, 30% 50%, 0% 50% )" }}>
                    <Box sx={{ width: "40%", height: "90%", display: "flex", justifyContent: "flex-start" }}>
                    </Box>
                    <Box sx={{ width: "30%", height: "90%", display: "flex", justifyContent: "center", alignItems: "center", }}>
                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <Typography style={{ fontSize: "clamp(1vw, 2vw, 2vw)", fontFamily: "Orbitron, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", }}> {userData?.company_name ? `${userData.company_name}` : "Loading..."}</Typography>
                        </motion.div>
                    </Box>
                    <Box sx={{ width: "40%", height: "100%", display: "flex", justifyContent: "flex-end", gap: "1%" }}>
                        <MapIcon sx={{
                            fontSize: "1.5vw", color: colors.bsf1, mt: "1%", mr: "2%", cursor: "pointer", transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out", "&:hover": { color: colors.bs3, transform: "scale(1.25)" }
                        }} onClick={() => navigate("/Map")} />
                        {(accessLevel === 3 || accessLevel === 2) && (
                            <>
                                <ExpandCircleDownIcon
                                    sx={{
                                        fontSize: "1.5vw",
                                        color: colors.bsf1, // Replace with your color variable
                                        mt: "1%",
                                        mr: "1%",
                                        cursor: "pointer",
                                        transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                                        "&:hover": {
                                            color: colors.bs3, // Replace with your hover color
                                            transform: "scale(1.25)"
                                        }
                                    }}
                                    onClick={handleClick}
                                />
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    sx={{
                                        "& .MuiMenu-paper": {
                                            backgroundColor: colors.bs7,
                                            border: "0.01vh solid",
                                            borderColor: colors.f1,
                                        },
                                        "& .MuiList-root": {
                                            backgroundColor: colors.bs7,
                                            padding: 0,
                                        },
                                    }}
                                >
                                    {clientList
                                        .map((client) => (
                                            <MenuItem
                                                key={client.client_id}
                                                onClick={() => handleSelect(client)}
                                                sx={{
                                                    fontSize: "0.8vw",
                                                    color: colors.f2,
                                                    backgroundColor: colors.bs7,
                                                    paddingY: "0.8vh",
                                                    minHeight: "unset",
                                                    border: "0.01vh solid",
                                                    borderColor: colors.bs2,
                                                    "&:hover": { backgroundColor: colors.bsf1, color: colors.f3 },
                                                }}
                                            >
                                                {client.company_name}
                                            </MenuItem>
                                        ))}

                                </Menu>
                            </>
                        )}
                        <ClearIcon sx={{
                            fontSize: "1.5vw", color: colors.bsf1, mt: "1%", mr: "2%", cursor: "pointer", transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out", "&:hover": { color: colors.bs3, transform: "scale(1.25)" }
                        }} onClick={() => navigate("/PlantList")} />
                    </Box>
                </Box>
                {/* Content*/}
                <Box sx={{ width: "100%", height: "82%", display: "flex", flexDirection: "row", gap: "1%", paddingLeft: "1%", paddingRight: "1%", }}>
                    {/* Left*/}
                    <Box sx={{ width: "20%", height: "100%", display: "flex", gap: "3%", flexDirection: "column" }}>
                        {/* Left-First Content*/}
                        <Box
                            sx={{
                                width: "100%",
                                height: "32.33%",
                                display: "flex",
                                gap: "2%",
                                flexDirection: "column",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "scale(1.05)",
                                }
                            }}
                        >

                            <Box sx={{ width: "100%", height: "24%", background: colors.bs3, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center", }}>
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography sx={{ fontSize: "1vw", fontWeight: "bold", color: "#ffffff", textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f", letterSpacing: "0.1vw", }}>Renewable Energy Goals</Typography>
                                </motion.div>
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "74%",
                                    background: colors.bs1,
                                    color: "white",
                                    boxShadow: 3,
                                    border: "0.1vw solid",
                                    borderColor: colors.bs2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    gap: "5%",
                                }}
                            >
                                {accumulatedSolar?.message == null || accumulatedSolar?.REC == null ? (
                                    <Typography
                                        sx={{
                                            fontSize: "1.1vw",
                                            color: colors.bsf1,
                                            fontFamily: "'Times New Roman', Times, serif",
                                        }}
                                    >
                                        Loading...
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography sx={{ fontSize: "1vw", color: colors.bsf1, textAlign: "center" }}>
                                            Accumulated RE Generated :
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.1vw",
                                                fontWeight: "bold",
                                                color: "#ffffff",
                                                textAlign: "center",
                                            }}
                                        >
                                            {Number(accumulatedSolar?.message || 0).toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })} kWh
                                        </Typography>

                                        <Typography sx={{ fontSize: "1vw", color: colors.bsf1, textAlign: "center" }}>
                                            Renewable Energy Certificates :
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.1vw",
                                                fontWeight: "bold",
                                                color: "#ffffff",
                                                textAlign: "center",
                                            }}
                                        >
                                            {Number(accumulatedSolar?.REC || 0).toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </Typography>
                                    </>
                                )}
                            </Box>

                        </Box>
                        {/* Left-Second Content*/}
                        <Box
                            sx={{
                                width: "100%",
                                height: "32.33%",
                                display: "flex",
                                gap: "2%",
                                flexDirection: "column",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "scale(1.05)",
                                },
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "24%",
                                    background: colors.bs3,
                                    color: "white",
                                    boxShadow: 3,
                                    border: "0.1vw solid",
                                    borderColor: colors.bs2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography
                                        sx={{
                                            fontSize: "1.1vw",
                                            fontFamily: "'Share Tech Mono', monospace",
                                            fontWeight: "bold",
                                            color: "#ffffff",
                                            textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f",
                                            letterSpacing: "0.1vw",
                                        }}
                                    >
                                        Energy Analysis
                                    </Typography>
                                </motion.div>
                            </Box>

                            {/* Chart */}
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "74%",
                                    background: colors.bs1,
                                    color: "white",
                                    boxShadow: 3,
                                    border: "0.1vw solid",
                                    borderColor: colors.bs2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        boxShadow: 3,
                                        border: "0.1vw solid",
                                        borderColor: colors.bs2,
                                    }}
                                >
                                    <Box sx={{ width: "100%", height: "100%", padding: "1%" }}>
                                        {hasSmartMeter ? (
                                            (() => {
                                                // For users with smart meter
                                                const linesToRender = [];
                                                if (combinedData.some(d => d.building_load_kw != null && d.building_load_kw !== 0)) {
                                                    linesToRender.push({ dataKey: "building_load_kw", color: colors.bsg1, name: "Building Load" });
                                                }
                                                if (combinedData.some(d => d.grid_power_kw != null && d.grid_power_kw !== 0)) {
                                                    linesToRender.push({ dataKey: "grid_power_kw", color: colors.bsg2, name: "Grid Value" });
                                                }
                                                if (combinedData.some(d => d.solar_generation_kw != null && d.solar_generation_kw !== 0)) {
                                                    linesToRender.push({ dataKey: "solar_generation_kw", color: colors.bsg3, name: "Solar Value" });
                                                }

                                                return (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={combinedData} margin={{ top: 10, right: 20, left: -20, bottom: 10 }}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke={colors.f1} />
                                                            <XAxis
                                                                dataKey="timestamp"
                                                                type="category"
                                                                interval="preserveStartEnd"
                                                                tick={{ fontSize: "0.5vw", fill: "white" }}
                                                                tickFormatter={(tick) => moment(tick).format("HH:mm")}
                                                            />
                                                            <YAxis tick={{ fontSize: "0.5vw", fill: "white" }} />
                                                            <Legend
                                                                verticalAlign="bottom"
                                                                align="center"
                                                                layout="horizontal"
                                                                wrapperStyle={{ left: "3%", top: "90%", transform: "translateY(-50%)", fontSize: "0.6vw" }}
                                                            />
                                                            {linesToRender.map(({ dataKey, color, name }) => (
                                                                <Line
                                                                    key={dataKey}
                                                                    type="monotone"
                                                                    dataKey={dataKey}
                                                                    stroke={color}
                                                                    strokeWidth={2}
                                                                    dot={false}
                                                                    name={name}
                                                                    connectNulls={true}
                                                                    animationDuration={1000}
                                                                />
                                                            ))}
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                );
                                            })()
                                        ) : (
                                            (() => {
                                                // Fallback chart without smart meter
                                                const fallbackLines = [
                                                    { dataKey: "grid_value", color: colors.bsg1, name: "Grid Value", data: gridWithEcosData },
                                                    { dataKey: "building_load", color: colors.bsg2, name: "Building Load", data: BuildingLoad },
                                                    { dataKey: "solar_value", color: colors.bsg3, name: "Solar Value", data: solarData },
                                                ];


                                                const linesToRenderFallback = fallbackLines.filter(line =>
                                                    line.data?.some(d => d[line.dataKey] != null && d[line.dataKey] !== 0)
                                                );

                                                return (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart
                                                            data={createChartData(
                                                                [gridWithEcosData, BuildingLoad, solarData],
                                                                ["grid_value", "building_load", "solar_value"]
                                                            )}
                                                            margin={{ top: 10, right: 20, left: -20, bottom: 10 }}
                                                        >
                                                            <CartesianGrid strokeDasharray="3 3" stroke={colors.f1} />
                                                            <XAxis
                                                                dataKey="timestamp"
                                                                type="category"
                                                                interval="preserveStartEnd"
                                                                tick={{ fontSize: "0.5vw", fill: "white" }}
                                                                tickFormatter={(tick) => moment(tick).format("HH:mm")}
                                                            />
                                                            <YAxis tick={{ fontSize: "0.5vw", fill: "white" }} />
                                                            <Legend
                                                                verticalAlign="bottom"
                                                                align="center"
                                                                layout="horizontal"
                                                                wrapperStyle={{ left: "3%", top: "90%", transform: "translateY(-50%)", fontSize: "0.6vw" }}
                                                            />
                                                            {linesToRenderFallback.map(({ dataKey, color, name }) => (
                                                                <Line
                                                                    key={dataKey}
                                                                    type="monotone"
                                                                    dataKey={dataKey}
                                                                    stroke={color}
                                                                    strokeWidth={2}
                                                                    dot={false}
                                                                    name={name}
                                                                    connectNulls={true}
                                                                    animationDuration={1000}
                                                                />
                                                            ))}
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                );
                                            })()
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        {/* Left-Third Content*/}
                        <Box
                            sx={{
                                width: "100%",
                                height: "32.33%",
                                display: "flex",
                                gap: "2%",
                                flexDirection: "column",
                                transition: "transform 0.2s ease-in-out", // Smooth transition for the whole box
                                "&:hover": {
                                    transform: "scale(1.05)", // Slightly enlarges the box on hover
                                }
                            }}
                        >
                            <Box sx={{ width: "100%", height: "24%", color: "white", boxShadow: 3, background: colors.bs3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography sx={{ fontSize: "1vw", fontFamily: "'Share Tech Mono', monospace", fontWeight: "bold", color: "#ffffff", textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f", letterSpacing: "0.1vw" }}>
                                        Consumption Used (Monthly)
                                    </Typography>
                                </motion.div>
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "74%",
                                    background: colors.bs1,
                                    color: "white",
                                    boxShadow: 3,
                                    border: "0.1vw solid",
                                    borderColor: colors.bs2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {isLoading || !solarAnalysis || !gridAnalysis ||
                                    Object.keys(solarAnalysis).length === 0 ||
                                    Object.keys(gridAnalysis).length === 0 ? (
                                    <Typography sx={{ fontSize: "1vw", color: colors.bsf1 }}>
                                        Loading...
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
                                        {/* Donut Chart on the Left */}
                                        <Box sx={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={donutData}
                                                        innerRadius="40%"
                                                        outerRadius="80%"
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        label={renderCustomLabel}
                                                        labelLine={false}  // <-- disables the connecting line
                                                        style={{ outline: "none" }}
                                                    >
                                                        {donutData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.sliceColor} />
                                                        ))}
                                                    </Pie>

                                                </PieChart>
                                            </ResponsiveContainer>


                                        </Box>

                                        {/* Text Values on the Right */}
                                        <Box sx={{ width: "50%", display: "flex", flexDirection: "column", justifyContent: "center", paddingLeft: "0vw", paddingRight: "1vw" }}>
                                            <Typography sx={{ fontSize: "0.9vw", color: colors.bsf1 }}>
                                                Solar Generated:
                                            </Typography>
                                            <Typography sx={{ fontSize: "1vw", mb: "1vh", fontWeight: 'bold' }}>
                                                {solar > 1000
                                                    ? `${(solar / 1000).toFixed(2)} mWh`
                                                    : `${solar.toFixed(2)} kWh`}
                                            </Typography>

                                            <Typography sx={{ fontSize: "0.9vw", color: colors.bsg1 }}>
                                                Grid Consumption:
                                            </Typography>
                                            <Typography sx={{ fontSize: "1vw", fontWeight: 'bold' }}>
                                                {grid > 1000
                                                    ? `${(grid / 1000).toFixed(2)} mWh`
                                                    : `${grid.toFixed(2)} kWh`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>


                        </Box>
                    </Box>
                    {/* Middle*/}
                    <Box sx={{ width: "59%", height: "100%", gap: "2%", display: "flex", flexDirection: "column" }}>
                        {/* Middle-TOP*/}
                        <Box sx={{ width: "100%", height: "79%", display: "flex", backgroundColor: "rgba(69, 116, 155, 0.45)", clipPath: "polygon(0% 0%, 13% 0%, 18% 7%, 82% 7%, 87% 0%, 100% 0%,100% 100%,0% 100%)" }}>

                            {userData?.client_id && <SLD clientId={userData.client_id} hasSmartMeter={hasSmartMeter} />}

                        </Box>
                        {/* Middle-BOTTOM*/}
                        <Box sx={{
                            width: "100%", height: "19%", display: "flex", flexDirection: "column", gap: "2%", clipPath: "polygon(0% 0%,100% 0%,100% 100%, 82.55% 100%, 81% 90%, 19% 90%, 17.5% 100%,0% 100% )", transition: "transform 0.2s ease-in-out",// Smooth transition for the whole box
                            "&:hover": {
                                transform: "scale(1.05)", // Slightly enlarges the box on hover
                            }
                        }}>
                            <Box sx={{ width: "100%", height: "29%", background: colors.bs3, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2 }}></Box>
                            <Box sx={{ width: "100%", height: "69%", background: colors.bs1, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", justifyContent: "center" }}>
                                <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", paddingTop: "1.3%", gap: "4%" }}>
                                    <Typography
                                        sx={{
                                            fontSize: "2vw",
                                            color: colors.bsf1,
                                            fontFamily: "'Orbitron', sans-serif",
                                            marginBottom: '0.5vw',
                                            textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f",
                                            letterSpacing: "0.1vw",
                                        }}
                                    >
                                        {format(time, 'yyyy-MM-dd')}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "2vw",
                                            color: colors.bsf1,
                                            fontFamily: "'Orbitron', sans-serif",
                                            textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f",
                                            letterSpacing: "0.1vw",
                                        }}
                                    >
                                        {time.toLocaleTimeString()}
                                    </Typography>

                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    {/* Right*/}
                    <Box sx={{ width: "20%", height: "100%", display: "flex", gap: "3%", flexDirection: "column" }}>
                        {/* Right-First Content*/}
                        <Box sx={{ width: "100%", height: "32.33%", display: "flex", gap: "2%", flexDirection: "column", transition: "transform 0.2s ease-in-out", "&:hover": { transform: "scale(1.05)", } }} >
                            <Box sx={{ width: "100%", height: "24%", background: colors.bs3, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography sx={{ fontSize: "1.1vw", fontWeight: "bold", color: "#ffffff", textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f", letterSpacing: "0.1vw", }}>Equivalent Trees (Monthly)</Typography>
                                </motion.div>
                            </Box>
                            <Box sx={{ width: "100%", height: "74%", background: colors.bs1, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Box sx={{ textAlign: "left", display: "flex", flexDirection: "column", }}>
                                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                        {!userData ? (
                                            <Typography sx={{ fontSize: "1vw", color: colors.bsf1, }}>
                                                Loading...
                                            </Typography>
                                        ) : (
                                            <>
                                                <Box sx={{ width: "17vw", mt: "1%", fontSize: "0.9vw", gap: "1vh", display: "flex", flexDirection: "column" }}>
                                                    <ImpactRow label="Solar" value={solarTrees} maxPositive={maxPositive} maxNegative={maxNegative} />
                                                    <ImpactRow label="Grid" value={gridTrees} maxPositive={maxPositive} maxNegative={maxNegative} />
                                                    <ImpactRow label="Load" value={loadTrees} maxPositive={maxPositive} maxNegative={maxNegative} />
                                                </Box>
                                            </>
                                        )}
                                    </motion.div>
                                </Box>
                            </Box>

                        </Box>
                        {/* Right-Second Content*/}
                        <Box sx={{
                            width: "100%", height: "32.33%", display: "flex", gap: "2%", flexDirection: "column", transition: "transform 0.2s ease-in-out", // Smooth transition for the whole box
                            "&:hover": {
                                transform: "scale(1.05)", // Slightly enlarges the box on hover
                            }
                        }}                        >
                            <Box sx={{ width: "100%", height: "24%", background: colors.bs3, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography sx={{ fontSize: "1.1vw", fontWeight: "bold", color: "#ffffff", textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f", letterSpacing: "0.1vw", }}>
                                        CO2 Emissions (Monthly)
                                    </Typography>
                                </motion.div>
                            </Box>
                            <Box
                                sx={{ width: "100%", height: "74%", background: colors.bs1, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center", }}>
                                {/* Check if any of the data is loading */}
                                {isLoading || !solarAnalysis || !gridAnalysis || !loadAnalysis ||
                                    Object.keys(solarAnalysis).length === 0 ||
                                    Object.keys(gridAnalysis).length === 0 ||
                                    Object.keys(loadAnalysis).length === 0 ? (
                                    <Typography
                                        sx={{ fontSize: "1vw", color: colors.bsf1, }}                                    >
                                        Loading...
                                    </Typography>
                                ) : (
                                    <>
                                        {/* Solar Data Section */}
                                        <Box sx={{ width: "30%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", }}>

                                            <Box sx={{ height: "30%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "1.5vw" }}                                            >
                                                ☀️
                                            </Box>
                                            <Box
                                                sx={{ height: "20%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}                                            >
                                                <Typography sx={{ fontSize: "0.9vw", fontWeight: "bold" }}>
                                                    Solar
                                                </Typography>
                                            </Box>
                                            <Box sx={{ height: "20%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}                                            >
                                                <Typography sx={{ fontSize: "1.1vw", fontWeight: "bold" }}                                                >
                                                    -{(parseFloat(solarAnalysis['co2_emissions_avoided_tonnes']) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{ height: "20%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}                                            >
                                                <Typography sx={{ fontSize: "0.9vw", }}>
                                                    Tonnes
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Grid Data Section */}
                                        <Box
                                            sx={{
                                                width: "30%",
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: "30%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    fontSize: "1.5vw",
                                                }}
                                            >
                                                ⚡
                                            </Box>
                                            <Box
                                                sx={{ height: "20%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}                                            >
                                                <Typography sx={{ fontSize: "0.9vw", fontWeight: "bold" }}>
                                                    Grid
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: "20%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "1.1vw",
                                                        fontWeight: "bold",

                                                    }}
                                                >
                                                    {(parseFloat(gridAnalysis['co2_emissions_avoided_tonnes']) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: "20%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "0.9vw", }}>
                                                    Tonnes
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Load Data Section */}
                                        <Box
                                            sx={{
                                                width: "30%",
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: "30%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    fontSize: "1.5vw",
                                                }}
                                            >
                                                🏭
                                            </Box>
                                            <Box
                                                sx={{ height: "20%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}                                            >
                                                <Typography sx={{ fontSize: "0.9vw", fontWeight: "bold" }}>
                                                    Load
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: "20%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "1.1vw",
                                                        fontWeight: "bold",

                                                    }}
                                                >
                                                    {(parseFloat(loadAnalysis['co2_emissions_avoided_tonnes']) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: "20%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "0.9vw" }}>
                                                    Tonnes
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>

                        </Box>
                        {/* Right-Third Content*/}
                        <Box sx={{ width: "100%", height: "32.33%", display: "flex", gap: "2%", flexDirection: "column", transition: "transform 0.2s ease-in-out", "&:hover": { transform: "scale(1.05)", } }}>
                            <Box sx={{ width: "100%", height: "24%", background: colors.bs3, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                    <Typography sx={{ fontSize: "1.1vw", fontFamily: "'Share Tech Mono', monospace", fontWeight: "bold", color: "#ffffff", textShadow: "0 0 0.5vw #0ff, 0 0 1vw #0ff, 0 0 1.5vw #00f", letterSpacing: "0.1vw", }}>
                                        RE Saving (Monthly)
                                    </Typography>
                                </motion.div>
                            </Box>
                            <Box sx={{ width: "100%", height: "74%", background: colors.bs1, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Box sx={{ textAlign: "left", display: "flex", flexDirection: "column" }}>
                                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                                        {!billDetail || (hasSmartMeter && !billDetaillastmonth) ? (
                                            <Typography
                                                sx={{
                                                    fontSize: "1.1vw",
                                                    color: colors.bsf1,
                                                    fontFamily: "'Times New Roman', Times, serif",
                                                }}
                                            >
                                                Loading...
                                            </Typography>
                                        ) : hasSmartMeter ? (

                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", flexDirection: "column", rowGap: "1vh" }}>

                                                <Typography sx={{ fontSize: "1.1vw", color: colors.bsf1, }}>
                                                    Percentage Saving:
                                                </Typography>
                                                <Typography sx={{ fontSize: "1.1vw", fontWeight: "bold" }}>
                                                    {((billDetail.estimated_original_total_bill - billDetail.estimated_net_total_bill) / billDetail.estimated_original_total_bill * 100).toFixed(2)} %
                                                </Typography>
                                                <Typography sx={{ fontSize: "1.1vw", color: colors.bsf1, }}>
                                                    RM Saving :
                                                </Typography>
                                                <Typography sx={{ fontSize: "1.1vw", fontWeight: "bold" }}>

                                                    RM {(billDetail.estimated_original_total_bill - billDetail.estimated_net_total_bill).toLocaleString("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </Typography>



                                            </Box>

                                        ) : (
                                            <>
                                                <Typography sx={{ fontSize: "1.1vw", color: colors.bsf1, fontFamily: "'Times New Roman', Times, serif", marginBottom: "1%" }}>
                                                    Solar Saving : RM{" "}
                                                    {(billDetail.estimated_original_total_bill - billDetail.estimated_before_battery_total_bill).toLocaleString("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </Typography>
                                                <Typography sx={{ fontSize: "1.1vw", color: colors.bsf1, fontFamily: "'Times New Roman', Times, serif", marginBottom: "1%" }}>
                                                    Battery Saving : RM{" "}
                                                    {(
                                                        (billDetail.estimated_original_total_bill - billDetail.estimated_net_total_bill) -
                                                        (billDetail.estimated_original_total_bill - billDetail.estimated_before_battery_total_bill)
                                                    ).toLocaleString("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </Typography>
                                                <Typography sx={{ fontSize: "1.1vw", color: colors.bsf1, fontFamily: "'Times New Roman', Times, serif", marginBottom: "1%" }}>
                                                    Total Saving : RM{" "}
                                                    {(billDetail.estimated_original_total_bill - billDetail.estimated_net_total_bill).toLocaleString("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </Typography>
                                            </>
                                        )}
                                    </motion.div>
                                </Box>



                            </Box>
                        </Box>
                    </Box>
                </Box>
                {/* Footer*/}
                <Box sx={{
                    width: "100%", height: "8%", display: "flex", justifyContent: "center", alignItems: "center", background: colors.bs4, color: "white", boxShadow: 3, border: "0.1vw solid", borderColor: colors.bs2, clipPath: "polygon(0% 100%, 100% 100%, 100% 50%, 69% 50%, 67% 0%, 33% 0%, 31% 50%, 0% 50%)"
                }}>
                    <Typography sx={{ fontSize: '0.7vw' }}>
                        This website is developed and maintained by AmSolar Sdn Bhd. All rights reserved. 2025.
                    </Typography>
                </Box>

                {isAutoLoopMode && allClients.length > 0 && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: '1.5vh',
                        right: '1.5vw',
                        zIndex: 1000
                    }}>
                        {/* Ultra Compact Panel */}
                        <Box sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(96, 166, 194, 0.4)',
                            borderRadius: '12px',
                            padding: '6px 10px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8,
                            minWidth: '250px'
                        }}>
                            {/* Mini Counter */}
                            <Typography sx={{
                                color: 'rgb(188, 253, 255)',
                                fontSize: '0.9vw',
                                fontWeight: 'bold',
                                minWidth: '30px'
                            }}>
                                {currentClientIndex + 1}/{allClients.length}
                            </Typography>

                            {/* Navigation */}
                            <IconButton onClick={goToPreviousClient} size="small" sx={{ padding: '3px' }}>
                                <SkipPreviousIcon sx={{ fontSize: '1vw', color: 'rgb(188, 253, 255)' }} />
                            </IconButton>

                            <IconButton
                                onClick={isAutoLoopPaused ? resumeAutoLoop : pauseAutoLoop}
                                size="small"
                                sx={{
                                    padding: '4px',
                                    color: isAutoLoopPaused ? '#FF9800' : '#4CAF50',
                                    border: `1px solid ${isAutoLoopPaused ? '#FF9800' : '#4CAF50'}`
                                }}
                            >
                                {isAutoLoopPaused ?
                                    <PlayArrowIcon sx={{ fontSize: '1.1vw' }} /> :
                                    <PauseIcon sx={{ fontSize: '1.1vw' }} />
                                }
                            </IconButton>

                            <IconButton onClick={goToNextClient} size="small" sx={{ padding: '3px' }}>
                                <SkipNextIcon sx={{ fontSize: '1vw', color: 'rgb(188, 253, 255)' }} />
                            </IconButton>

                            {/* Speed */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: '0.7vw', color: 'rgba(255, 255, 255, 0.6)' }} />
                                <Typography sx={{ color: 'white', fontSize: '0.7vw', fontWeight: 'bold' }}>
                                    {autoPlaySpeed}s
                                </Typography>
                            </Box>

                            {/* Status Dot */}
                            <Box sx={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                backgroundColor: isAutoLoopMode ?
                                    (isAutoLoopPaused ? '#FF9800' : '#4CAF50') : '#f44336',
                                ml: 0.5,
                                animation: isAutoLoopMode && !isAutoLoopPaused ? 'pulse 1.5s infinite' : 'none'
                            }} />

                            {/* Stop */}
                            <IconButton
                                onClick={() => { stopAutoLoop(); navigate("/Map"); }}
                                size="small"
                                sx={{ padding: '3px', ml: 0.5, color: '#f44336' }}
                            >
                                <StopIcon sx={{ fontSize: '1vw' }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box >


    );
};

export default BigScreen;
